import React from 'react'
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import StatusIcon from '@mui/icons-material/VerifiedUser'
import AmbulanceIcon from '../../../../assets/icons/Ambulance'
import LockOpen from '@mui/icons-material/LockOpen'
import Lock from '@mui/icons-material/Lock'
import MedicalServices from '@mui/icons-material/MedicalServices'
import EventIcon from '@mui/icons-material/Event'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { useNavigate } from 'react-router-dom'

const levelColor = (level) => {
  switch (level) {
    case 'Advanced':
      return '#DC2626'
    case 'Intermediate':
      return '#9B287B'
    case 'Specialist':
      return '#1F3D7C'
    default:
      return '#1E7F88'
  }
}

const assignmentLabel = (bag) => {
  if (bag.assignedToEmployee) {
    return `${bag.assignedToEmployee.userAccount.firstName} ${bag.assignedToEmployee.userAccount.lastName}`
  }
  if (bag.assignedToVehicle) return bag.assignedToVehicle.callSign
  if (bag.assignedToEvent) return `Event: ${bag.assignedToEvent.title}`
  if (bag.assignedToStore) return `Store: ${bag.assignedToStore.name}`
  return 'Unassigned'
}

const AssignmentIcon = ({ bag, color }) => {
  if (bag.assignedToEmployee) {
    return (
      <Avatar
        src={bag.assignedToEmployee.userAccount.photograph}
        sx={{ width: 24, height: 24, borderRadius: 1 }}
      />
    )
  }

  if (bag.assignedToVehicle) return <AmbulanceIcon />
  if (bag.assignedToEvent) return <EventIcon sx={{ color }} />
  if (bag.assignedToStore) return <StatusIcon sx={{ color }} />
  return <MedicalServices sx={{ color }} />
}

const DrugsBagCard = ({ bag }) => {
  const navigate = useNavigate()
  const color = levelColor(bag.level)
  const sealRemoved = bag.tagNumber === 'Removed'

  return (
    <Grid item xs={12} sm={6} lg={4} xl={3}>
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          // No global CssBaseline, so Paper is content-box by default —
          // border-box stops height:100% + padding overflowing the grid row.
          boxSizing: 'border-box',
          p: 1.25,
          borderRadius: 2,
          border: '1px solid rgba(15,23,42,0.08)',
          borderLeft: `5px solid ${color}`,
          backgroundColor: '#fff',
        }}
      >
        <Stack spacing={1.25} height="100%">
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                color,
                backgroundColor: `${color}14`,
                flexShrink: 0,
              }}
            >
              <AssignmentIcon bag={bag} color={color} />
            </Box>

            <Stack minWidth={0} flex={1}>
              <Typography fontWeight={900} noWrap>
                {bag.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {assignmentLabel(bag)}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {bag.level && (
              <Chip
                size="small"
                label={bag.level}
                sx={{
                  height: 22,
                  borderRadius: 1,
                  color,
                  fontWeight: 850,
                  backgroundColor: `${color}12`,
                }}
              />
            )}
            {bag.number && (
              <Chip
                size="small"
                label={`Bag ${bag.number}`}
                sx={{ height: 22, borderRadius: 1, fontWeight: 800 }}
              />
            )}
            <Chip
              size="small"
              icon={sealRemoved ? <LockOpen /> : <Lock />}
              label={bag.tagNumber || 'No seal'}
              color={sealRemoved ? 'error' : 'success'}
              sx={{ height: 22, borderRadius: 1, fontWeight: 800 }}
            />
          </Stack>

          <Box flex={1} />

          <Button
            endIcon={<OpenInNew />}
            variant="contained"
            fullWidth
            onClick={() => navigate(bag._id)}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#1E7F88',
            }}
          >
            Open bag
          </Button>
        </Stack>
      </Paper>
    </Grid>
  )
}

export default DrugsBagCard
